import type { ReactElement } from "react";

type IconProps = {
  size?: number;
  fill?: string;
  sw?: number;
};

type PathInput = string | string[];

const I = ({
  d,
  size = 22,
  fill = "none",
  sw = 1.5,
}: IconProps & { d: PathInput }): ReactElement => (
  <svg
    className="ic"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

// The Icon object map below remains unchanged — every entry is typed via inference from `I`.
const Icon = {
  /* navigation / chrome */
  arrowRight: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M5 12h14", "M13 6l6 6-6 6"]} />
  ),
  chevronDown: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M6 9l6 6 6-6"]} />
  ),
  chevronRight: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M9 6l6 6-6 6"]} />
  ),
  menu: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M3 6h18M3 12h18M3 18h18"]} />
  ),
  x: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M18 6L6 18M6 6l12 12"]} />
  ),
  plus: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M12 5v14M5 12h14"]} />
  ),
  check: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M5 12l4 4L19 6"]} />
  ),

  /* domain */
  user: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={[
        "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
        "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
        "M22 11l-3 3-2-2",
      ]}
    />
  ),
  tools: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={["M14.7 6.3a4 4 0 0 1 5.3 5.3l-9 9-5.3.7.7-5.3 8.3-9.7z", "M13 7l4 4"]}
    />
  ),
  chart: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M3 3v18h18", "M7 14l3-3 3 3 5-5"]} />
  ),
  card: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={[
        "M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z",
        "M3 10h18",
        "M7 15h3",
      ]}
    />
  ),
  bell: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={["M6 8a6 6 0 1 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9z", "M10 21a2 2 0 0 0 4 0"]}
    />
  ),
  brush: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M3 21c0-3 2-5 5-5s3 4 6 4 4-2 4-2", "M14 4l6 6-9 9H5v-6l9-9z"]} />
  ),
  coin: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={[
        "M12 21c5 0 9-4 9-9s-4-9-9-9-9 4-9 9 4 9 9 9z",
        "M12 7v10",
        "M15 9.5c0-1.4-1.3-2.5-3-2.5s-3 1.1-3 2.5S10.3 12 12 12s3 1.1 3 2.5-1.3 2.5-3 2.5-3-1.1-3-2.5",
      ]}
    />
  ),
  bolt: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M13 2L4 14h7l-1 8 9-12h-7l1-8z"]} />
  ),
  refund: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M3 12a9 9 0 1 0 3-6.7", "M3 4v5h5"]} />
  ),
  qr: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={[
        "M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3z",
        "M14 14h3v3h-3z",
        "M20 14v3M14 20h3M17 17v4M20 20h1",
      ]}
    />
  ),
  cycle: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M21 12a9 9 0 1 1-3-6.7", "M21 4v5h-5"]} />
  ),
  shield: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={["M12 3l8 3v6c0 4.5-3.2 8.4-8 9-4.8-.6-8-4.5-8-9V6l8-3z", "M9 12l2 2 4-4"]}
    />
  ),
  arrow: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M5 12h14", "M13 6l6 6-6 6"]} />
  ),
  invoice: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M6 3h9l3 3v15H6V3z", "M14 3v4h4", "M9 12h6M9 16h6"]} />
  ),
  split: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M3 6h5l8 12h5", "M16 6h5", "M19 3l3 3-3 3", "M3 18h5l3-4.5"]} />
  ),
  cart: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={[
        "M3 4h2l2.6 12.3a2 2 0 0 0 2 1.7h7.7a2 2 0 0 0 2-1.6L21 8H6",
        "M10 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM18 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
      ]}
    />
  ),
  link: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={[
        "M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1",
        "M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1",
      ]}
    />
  ),
  globe: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={[
        "M12 21c5 0 9-4 9-9s-4-9-9-9-9 4-9 9 4 9 9 9z",
        "M3 12h18",
        "M12 3c3 3.5 4 7.5 4 9s-1 5.5-4 9c-3-3.5-4-7.5-4-9s1-5.5 4-9z",
      ]}
    />
  ),
  lock: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M5 11h14v10H5z", "M8 11V7a4 4 0 1 1 8 0v4"]} />
  ),
  trending: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M3 17l6-6 4 4 8-8", "M14 7h7v7"]} />
  ),
  send: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I size={s} sw={sw} d={["M22 2L11 13", "M22 2l-7 20-4-9-9-4 20-7z"]} />
  ),
  sun: ({ s = 22, sw }: { s?: number; sw?: number }) => (
    <I
      size={s}
      sw={sw}
      d={[
        "M12 4V2M12 22v-2M4 12H2M22 12h-2M5.6 5.6L4.2 4.2M19.8 19.8l-1.4-1.4M5.6 18.4L4.2 19.8M19.8 4.2l-1.4 1.4",
        "M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z",
      ]}
    />
  ),
};

export { Icon };
